import { ReactNode, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Offcanvas, Spinner } from "react-bootstrap";
import Select from "react-select";

import {
  FixedToolbarItem,
  Toolbar,
  useReactSelectTheme,
  useToastManager,
} from "@bagel-web/components";

type AddProjectUserDrawerProps<User extends { email: string }> = {
  show: boolean;
  onClose: () => void;

  // Source data — owned by the parent so it can pick the right fetcher
  // (e.g. a paginated `/users` endpoint vs. an organization document) and
  // invalidate its own caches.
  organizationUsers: User[];
  isLoadingUsers: boolean;
  loadUsersError?: Error | null;
  // Emails already on the project, so they are filtered out of the picker.
  excludedEmails: Set<string>;

  // Rendering hooks. Parent provides these because the shape of `User` and
  // the per-app `UserRow` differ between Dashboard and Admin.
  getSearchLabel: (user: User) => string;
  renderUserOption: (user: User) => ReactNode;
  renderUserChip: (user: User) => ReactNode;

  // Adds a single user. The drawer batches these via Promise.allSettled so
  // failures are reported individually and partial success is preserved.
  addUser: (email: string) => Promise<void>;
  // Called after the batch finishes (any outcome). Use this to invalidate
  // query caches in the parent.
  onAddsCompleted?: () => void;
};

type UserOption<User> = {
  label: string;
  value: string;
  user: User;
};

const AddProjectUserDrawer = <User extends { email: string }>({
  show,
  onClose,
  organizationUsers,
  isLoadingUsers,
  loadUsersError,
  excludedEmails,
  getSearchLabel,
  renderUserOption,
  renderUserChip,
  addUser,
  onAddsCompleted,
}: AddProjectUserDrawerProps<User>) => {
  const { publishToast } = useToastManager();
  const reactSelectTheme = useReactSelectTheme();

  const [selectedUsers, setSelectedUsers] = useState<
    readonly UserOption<User>[]
  >([]);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (!show) {
      setSelectedUsers([]);
    }
  }, [show]);

  const userOptions: UserOption<User>[] = useMemo(
    () =>
      organizationUsers
        .filter((u) => !excludedEmails.has(u.email))
        .map((u) => ({
          label: getSearchLabel(u),
          value: u.email,
          user: u,
        })),
    [organizationUsers, excludedEmails, getSearchLabel]
  );

  const handleConfirm = async () => {
    if (selectedUsers.length === 0) return;
    setIsAdding(true);
    try {
      const results = await Promise.allSettled(
        selectedUsers.map((u) => addUser(u.value))
      );
      onAddsCompleted?.();

      const failures = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
      );
      const succeededCount = results.length - failures.length;

      if (succeededCount > 0) {
        publishToast(
          `${succeededCount} ${succeededCount === 1 ? "user" : "users"} added to project`
        );
      }
      failures.forEach((f) => {
        const reason = f.reason;
        publishToast(
          `Failed to add user to project: ${reason instanceof Error ? reason.message : reason}`
        );
      });

      if (failures.length === 0) {
        onClose();
      }
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Offcanvas show={show} placement="end" onHide={onClose}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Add Users to Project</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {loadUsersError && (
          <Alert variant="warning">
            Could not load users: {loadUsersError.message}.
          </Alert>
        )}
        <Form.Label>Users</Form.Label>
        <Select<UserOption<User>, true>
          isMulti
          theme={reactSelectTheme}
          options={userOptions}
          value={selectedUsers}
          onChange={(options) => setSelectedUsers(options)}
          isLoading={isLoadingUsers}
          isClearable
          isDisabled={isLoadingUsers || isAdding}
          placeholder="Search users..."
          noOptionsMessage={() =>
            isLoadingUsers ? "Loading..." : "No matching users found."
          }
          closeMenuOnSelect={false}
          formatOptionLabel={(option, { context }) =>
            context === "menu"
              ? renderUserOption(option.user)
              : renderUserChip(option.user)
          }
        />
        <Toolbar className="mt-3" style={{ justifyContent: "end" }}>
          <FixedToolbarItem>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={selectedUsers.length === 0 || isAdding}
            >
              {isAdding ? <Spinner size="sm" /> : "Confirm"}
            </Button>
          </FixedToolbarItem>
        </Toolbar>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddProjectUserDrawer;
